import { Link, useNavigate } from "@tanstack/react-router";
import { Button, Typography } from "antd";
import AccessDenied from "assets/access_denied.svg";
import { metaAtom } from "atoms/metaAtom";
import {
    HeaderNavigationDetailLink,
    HeaderNavigationEditLink,
    HeaderNavigationItem,
    headerNavigationAtom,
} from "components/UI/DynamicHeaderNavigation/atoms/headerNavigationAtom.ts";
import { TenantLink } from "components/UI/TenantLink/TenantLink";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useRoute } from "hooks/useRoute";
import { useTenantNavigate } from "hooks/useTenantNavigate";
import { useUser } from "hooks/useUser.ts";
import React, { useEffect } from "react";
import { useResetRecoilState, useSetRecoilState } from "recoil";
import { RequiredStaticData } from "utils/createPaldockFileRoute.ts";
import { isRouteAccessible } from "utils/isRouteAccessible.ts";
import { removeTenantSlugParam } from "utils/removeTenantSlugParam.ts";

export interface PaldockPageProps {
    headerNavigationItems?: () => HeaderNavigationItem[];
    editLink?: HeaderNavigationEditLink | null;
    detailLink?: HeaderNavigationDetailLink | null;
    saveFormId?: string | null;
    onDelete?: (() => void) | null;
    deleteLoading?: boolean;
    title?: string | null;
    isEdit?: boolean;
    extraButtons?: React.ReactNode;
    children: React.ReactNode;
}

export const PaldockPage = ({
    headerNavigationItems,
    editLink = null,
    detailLink = null,
    saveFormId = null,
    onDelete,
    deleteLoading,
    title = null,
    isEdit = false,
    extraButtons,
    children,
}: PaldockPageProps) => {
    const { t } = usePaldockTranslation("ui.no-access");
    const setHeaderNavigationState = useSetRecoilState(headerNavigationAtom);
    const resetHeaderNavigationState = useResetRecoilState(headerNavigationAtom);
    const setMeta = useSetRecoilState(metaAtom);
    const resetMeta = useResetRecoilState(metaAtom);
    const permissionsUtils = usePermission();
    const routeUtils = useRoute();
    const currentRoute = routeUtils.getCurrentRoute();
    const staticData = currentRoute?.staticData as RequiredStaticData & Record<string, unknown>;
    const userUtils = useUser();
    const tenantSlug = userUtils.getActiveTenant()?.slug_name;
    const tenantNavigate = useTenantNavigate();
    const navigate = useNavigate();

    useEffect(() => {
        if (staticData.redirects) {
            if (staticData.defaultRedirect) {
                const defaultRedirectStaticData = staticData.defaultRedirect.options.staticData as RequiredStaticData;
                if (defaultRedirectStaticData) {
                    if (isRouteAccessible(defaultRedirectStaticData, permissionsUtils.hasAtLeastOnePermission)) {
                        navigateToRoute(staticData.defaultRedirect.fullPath);
                        return;
                    }
                }
            }
            for (const route of staticData.redirects) {
                const routePermissionsStaticData = route.options.staticData as RequiredStaticData;
                if (routePermissionsStaticData) {
                    if (isRouteAccessible(routePermissionsStaticData, permissionsUtils.hasAtLeastOnePermission)) {
                        navigateToRoute(route.fullPath);
                        return;
                    }
                }
            }
        }
    }, []);

    useEffect(() => {
        const navigationItems = headerNavigationItems ? headerNavigationItems() : [];
        setHeaderNavigationState({
            items: navigationItems,
            editLink: editLink,
            detailLink: detailLink,
            saveFormId: saveFormId,
            onDelete: onDelete,
            deleteLoading: deleteLoading,
            isEdit: isEdit,
            extraButtons: extraButtons,
        });
        return () => resetHeaderNavigationState();
    }, [
        headerNavigationItems,
        editLink,
        detailLink,
        saveFormId,
        onDelete,
        deleteLoading,
        isEdit,
        extraButtons,
        setHeaderNavigationState,
        resetHeaderNavigationState,
    ]);

    useEffect(() => {
        if (title) {
            setMeta({ title });
        } else {
            resetMeta();
        }
    }, [title, setMeta, resetMeta]);

    if (!isRouteAccessible(staticData, permissionsUtils.hasAtLeastOnePermission)) {
        return (
            <div className={"fixed inset-0 flex items-center justify-center bg-white z-50"}>
                <div className={"flex items-center justify-center flex-col"}>
                    <img src={AccessDenied} alt={t("alt")} className={"my-[24px]"} />
                    <Typography.Title level={4} className={"mb-0"}>
                        {t("title")}
                    </Typography.Title>
                    <p className={"mb-[30px] text-black/45"}>{t("message")}</p>
                    {tenantSlug ? (
                        <TenantLink to={"/"}>
                            <Button type="primary">{t("go_to_dashboard")}</Button>
                        </TenantLink>
                    ) : (
                        <Link to="/account">
                            <Button type="primary">{t("go_to_dashboard")}</Button>
                        </Link>
                    )}
                </div>
            </div>
        );
    }

    return children;

    function navigateToRoute(fullPath: string) {
        if (tenantSlug) {
            tenantNavigate(removeTenantSlugParam(fullPath), {
                replace: true,
                params: currentRoute.params,
            });
        } else {
            navigate({ to: fullPath, replace: true });
        }
    }
};
